import { useEffect, useState } from 'react';
import {
  getCreditControlSettings,
  saveCreditControlSettings,
  type CreditControlSettings,
} from '../orcamentos/orcamentosApi';

// C# property-initializer defaults (singleton load-or-new, sem seed) — espelham o backend.
const DEFAULTS: CreditControlSettings = {
  riskYellowOverdueDays: 15,
  riskYellowOverdueAmount: 1000,
  riskRedOverdueDays: 60,
  riskRedOverdueAmount: 5000,
  riskRedOnLegalProcess: true,
  blockOnOverdueAmount: true,
  blockOverdueAmountThreshold: 5000,
  blockOnCreditLimitExceeded: true,
  blockOnRedRisk: false,
  jalOverdueDays: 60,
  collectionReminderLeadDays: 7,
};

// Campos numéricos editados como string (clear não pode zerar/silenciar — padrão anomalias).
type NumericField =
  | 'riskYellowOverdueDays'
  | 'riskYellowOverdueAmount'
  | 'riskRedOverdueDays'
  | 'riskRedOverdueAmount'
  | 'blockOverdueAmountThreshold'
  | 'jalOverdueDays'
  | 'collectionReminderLeadDays';

type BooleanField =
  | 'riskRedOnLegalProcess'
  | 'blockOnOverdueAmount'
  | 'blockOnCreditLimitExceeded'
  | 'blockOnRedRisk';

interface CreditControlForm {
  riskYellowOverdueDays: string;
  riskYellowOverdueAmount: string;
  riskRedOverdueDays: string;
  riskRedOverdueAmount: string;
  riskRedOnLegalProcess: boolean;
  blockOnOverdueAmount: boolean;
  blockOverdueAmountThreshold: string;
  blockOnCreditLimitExceeded: boolean;
  blockOnRedRisk: boolean;
  jalOverdueDays: string;
  collectionReminderLeadDays: string;
}

const formFromSettings = (s: CreditControlSettings): CreditControlForm => ({
  riskYellowOverdueDays: String(s.riskYellowOverdueDays),
  riskYellowOverdueAmount: String(s.riskYellowOverdueAmount),
  riskRedOverdueDays: String(s.riskRedOverdueDays),
  riskRedOverdueAmount: String(s.riskRedOverdueAmount),
  riskRedOnLegalProcess: s.riskRedOnLegalProcess,
  blockOnOverdueAmount: s.blockOnOverdueAmount,
  blockOverdueAmountThreshold: String(s.blockOverdueAmountThreshold),
  blockOnCreditLimitExceeded: s.blockOnCreditLimitExceeded,
  blockOnRedRisk: s.blockOnRedRisk,
  jalOverdueDays: String(s.jalOverdueDays),
  collectionReminderLeadDays: String(s.collectionReminderLeadDays),
});

const labelClass = 'block text-foreground mb-2';
const labelStyle = {
  fontSize: 'var(--text-caption)',
  fontWeight: 'var(--font-weight-semi-bold)',
} as const;
const inputClass =
  'w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
const inputStyle = {
  borderRadius: 'var(--radius-button)',
  fontSize: 'var(--text-base)',
  maxWidth: 200,
} as const;
const helpClass = 'mt-1 text-secondary-text';
const helpStyle = { fontSize: 'var(--text-caption)' } as const;
const sectionTitleStyle = {
  fontSize: 'var(--text-section-title)',
  fontWeight: 'var(--font-weight-semi-bold)',
} as const;

export default function CreditControlSettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState<CreditControlForm>(formFromSettings(DEFAULTS));

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getCreditControlSettings()
      .then((data) => setForm(formFromSettings(data)))
      .catch(() =>
        setLoadError('Não foi possível carregar a configuração de controlo de crédito.'),
      )
      .finally(() => setIsLoading(false));
  }, []);

  const setNumber = (field: NumericField, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const setBoolean = (field: BooleanField, value: boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    // Parse + valida os campos numéricos (não-negativos). Limpar não pode zerar em silêncio.
    const numericEntries: [NumericField, string][] = [
      ['riskYellowOverdueDays', form.riskYellowOverdueDays],
      ['riskYellowOverdueAmount', form.riskYellowOverdueAmount],
      ['riskRedOverdueDays', form.riskRedOverdueDays],
      ['riskRedOverdueAmount', form.riskRedOverdueAmount],
      ['blockOverdueAmountThreshold', form.blockOverdueAmountThreshold],
      ['jalOverdueDays', form.jalOverdueDays],
      ['collectionReminderLeadDays', form.collectionReminderLeadDays],
    ];
    const parsed: Record<NumericField, number> = {} as Record<NumericField, number>;
    for (const [field, raw] of numericEntries) {
      const value = Number(raw.trim());
      if (!Number.isFinite(value) || value < 0) {
        setSaveError('Todos os limiares (dias e valores) têm de ser números maiores ou iguais a 0.');
        return;
      }
      parsed[field] = value;
    }

    const payload: CreditControlSettings = {
      riskYellowOverdueDays: parsed.riskYellowOverdueDays,
      riskYellowOverdueAmount: parsed.riskYellowOverdueAmount,
      riskRedOverdueDays: parsed.riskRedOverdueDays,
      riskRedOverdueAmount: parsed.riskRedOverdueAmount,
      riskRedOnLegalProcess: form.riskRedOnLegalProcess,
      blockOnOverdueAmount: form.blockOnOverdueAmount,
      blockOverdueAmountThreshold: parsed.blockOverdueAmountThreshold,
      blockOnCreditLimitExceeded: form.blockOnCreditLimitExceeded,
      blockOnRedRisk: form.blockOnRedRisk,
      jalOverdueDays: parsed.jalOverdueDays,
      collectionReminderLeadDays: parsed.collectionReminderLeadDays,
    };

    setIsSaving(true);
    try {
      const updated = await saveCreditControlSettings(payload);
      setForm(formFromSettings(updated));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível guardar a configuração. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-secondary-text py-8 text-center" style={{ fontSize: 'var(--text-base)' }}>
        A carregar configuração...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  const numberField = (
    field: NumericField,
    label: string,
    help: string,
  ) => (
    <div>
      <label className={labelClass} style={labelStyle}>
        {label}
      </label>
      <input
        type="number"
        value={form[field]}
        onChange={(e) => setNumber(field, e.target.value)}
        min={0}
        step={1}
        className={inputClass}
        style={inputStyle}
      />
      <p className={helpClass} style={helpStyle}>
        {help}
      </p>
    </div>
  );

  const toggleField = (field: BooleanField, label: string) => (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input
        type="checkbox"
        checked={form[field]}
        onChange={(e) => setBoolean(field, e.target.checked)}
      />
      {label}
    </label>
  );

  return (
    <div className="space-y-6">
      <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
        Regras de risco do cliente, bloqueio de novas faturas e workflow de cobrança. Aplicadas ao
        emitir faturas, ao calcular o risco e ao sugerir ações no painel de Cobrança.
      </p>

      {/* Risco */}
      <div className="space-y-4">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Risco
        </h4>
        {numberField(
          'riskYellowOverdueDays',
          'Dias em atraso para risco médio',
          'Acima destes dias de atraso (na pior fatura), o cliente fica pelo menos em risco médio (amarelo). Default: 15.',
        )}
        {numberField(
          'riskYellowOverdueAmount',
          'Dívida vencida para risco médio (€)',
          'Dívida vencida igual ou superior a este valor coloca o cliente pelo menos em risco médio. Default: 1000.',
        )}
        {numberField(
          'riskRedOverdueDays',
          'Dias em atraso para risco alto',
          'Acima destes dias de atraso, o cliente fica em risco alto (vermelho). Default: 60.',
        )}
        {numberField(
          'riskRedOverdueAmount',
          'Dívida vencida para risco alto (€)',
          'Dívida vencida igual ou superior a este valor coloca o cliente em risco alto. Default: 5000.',
        )}
        {toggleField('riskRedOnLegalProcess', 'Processo jurídico ativo ⇒ risco alto')}
      </div>

      {/* Bloqueio */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Bloqueio de emissão
        </h4>
        {toggleField('blockOnOverdueAmount', 'Bloquear quando a dívida vencida atinge o limite')}
        {numberField(
          'blockOverdueAmountThreshold',
          'Limite de dívida vencida para bloqueio (€)',
          'Dívida vencida igual ou superior a este valor bloqueia a emissão de novas faturas. Default: 5000.',
        )}
        {toggleField(
          'blockOnCreditLimitExceeded',
          'Bloquear quando saldo + nova fatura excede o limite de crédito',
        )}
        {toggleField('blockOnRedRisk', 'Bloquear quando o cliente está em risco alto')}
        <p className={helpClass} style={helpStyle}>
          Um bloqueio pode ser autorizado caso a caso, com motivo, no momento da emissão.
        </p>
      </div>

      {/* JAL & Cobrança */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          JAL &amp; Cobrança
        </h4>
        {numberField(
          'jalOverdueDays',
          'Dias em atraso para migração JAL',
          'Dias mínimos de atraso antes de uma fatura poder ser migrada para a conta corrente (JAL). Default: 60.',
        )}
        {numberField(
          'collectionReminderLeadDays',
          'Antecedência do lembrete pré-vencimento (dias)',
          'Dias antes do vencimento em que o lembrete "pré-vencimento" passa a ser sugerido. Default: 7.',
        )}
      </div>

      {/* Erros e feedback */}
      {saveError && <div className="text-destructive text-sm">{saveError}</div>}
      {saveSuccess && (
        <div className="text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>
          ✓ Configuração guardada com sucesso.
        </div>
      )}

      {/* Botão guardar */}
      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
